import { FxIcon } from '@/components/fx'

import { formatSize, type UploadItem } from './uploadQueue'

/**
 * The queue, one row per file, as the artboard draws it: glyph, name,
 * state, remove. A row that failed also offers a retry of its own, because
 * one 503 in the middle of thirty scans must not mean starting over.
 *
 * A list, not a table: it is one thing per row, and `<ul>` is what a screen
 * reader needs to announce "3 von 12".
 */
export function UploadFileList({
  items,
  onRemove,
  onRetry,
}: {
  items: UploadItem[]
  onRemove: (id: string) => void
  onRetry: (id: string) => void
}) {
  if (items.length === 0) return null

  return (
    <ul className="m-0 flex list-none flex-col border-t-2 border-divider p-0">
      {items.map((item) => (
        <li
          key={item.id}
          className="flex min-h-[44px] flex-wrap items-center gap-3 border-b border-divider py-2 last:border-b-0"
        >
          <Glyph item={item} />
          <span
            className="min-w-0 flex-1 truncate sm:w-[250px] sm:flex-none"
            title={`${item.name} · ${formatSize(item.sizeBytes)}`}
          >
            {item.name}
          </span>
          <State item={item} />
          {item.status === 'failed' && (
            <button
              type="button"
              onClick={() => onRetry(item.id)}
              className="h-8 shrink-0 rounded-md bg-primary-subtle px-3 text-small font-semibold text-primary-text"
            >
              Erneut versuchen
            </button>
          )}
          <button
            type="button"
            aria-label={`${item.name} aus der Liste entfernen`}
            onClick={() => onRemove(item.id)}
            className="flex shrink-0 rounded-sm text-text-muted"
          >
            <FxIcon name="close" size={20} />
          </button>
        </li>
      ))}
    </ul>
  )
}

function Glyph({ item }: { item: UploadItem }) {
  if (item.status === 'done') {
    return <FxIcon name="check_circle" size={22} className="shrink-0 text-success-text" />
  }
  if (item.status === 'failed' || item.status === 'tooLarge' || item.status === 'unsupported') {
    return <FxIcon name="error" size={22} className="shrink-0 text-danger-text" />
  }
  if (item.status === 'blocked') {
    return <FxIcon name="pause_circle" size={22} className="shrink-0 text-warning-text" />
  }
  return (
    <FxIcon
      name={item.isPdf ? 'picture_as_pdf' : 'upload'}
      size={22}
      className="shrink-0 text-primary-strong"
    />
  )
}

function State({ item }: { item: UploadItem }) {
  switch (item.status) {
    case 'done':
      return <span className="flex-1 text-small text-success-text">hochgeladen</span>
    case 'uploading': {
      const percent = Math.round(item.progress * 100)
      return (
        <span className="flex flex-1 items-center gap-3">
          <span
            role="progressbar"
            aria-label={`${item.name} wird hochgeladen`}
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
            className="h-1 min-w-[80px] flex-1 rounded-full bg-fill"
          >
            <span
              className="block h-1 rounded-full bg-primary-strong"
              style={{ width: `${percent}%` }}
            />
          </span>
          <span className="text-small tabular-nums">{percent} %</span>
        </span>
      )
    }
    case 'queued':
      return <span className="flex-1 text-small text-text-muted">wartet</span>
    case 'blocked':
      return <span className="flex-1 text-small text-warning-text">zurückgestellt</span>
    default:
      return <span className="flex-1 text-small text-danger-text">{item.error}</span>
  }
}
